import { useId, useState } from "react";
import { useLedger, useLedgerAccounts, type Period } from "@/api/books";
import { RecordLink } from "@/components/books/BooksLink";
import { PeriodPicker, thisFy } from "@/components/books/PeriodPicker";
import { RequestError } from "@/components/RequestError";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField } from "@/components/ui/field";
import { formatDate, formatInr, formatMoney } from "@/lib/format";

const NUM = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

export function LedgerPanel() {
  const id = useId();
  const accounts = useLedgerAccounts(true);
  const [accountId, setAccountId] = useState("");
  const [period, setPeriod] = useState<Period>(thisFy());
  const ledger = useLedger(accountId ? { accountId, ...period } : null);
  const options = (accounts.data ?? []).map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          id={`${id}-account`}
          label="Account"
          value={accountId}
          onChange={setAccountId}
          options={options}
          placeholder="Choose an account"
          className="min-w-64"
        />
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      <RequestError error={accounts.error ?? ledger.error} />
      {!accountId && (
        <EmptyState title="Pick an account" description="Its entries over the period, with a running balance." />
      )}
      {ledger.data && (
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Entry</th>
                <th className="px-4 py-2 font-medium">Narration</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 text-right font-medium">Debit</th>
                <th className="px-4 py-2 text-right font-medium">Credit</th>
                <th className="px-4 py-2 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-muted/30">
                <td className="px-4 py-2" colSpan={6}>
                  Opening balance
                </td>
                <td className={NUM}>{formatInr(ledger.data.opening_inr)}</td>
              </tr>
              {ledger.data.rows.map((row, index) => (
                <tr key={`${row.entry_id}:${index}`} className="border-b align-top last:border-0">
                  <td className="whitespace-nowrap px-4 py-2">{formatDate(row.entry_date)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">{row.label}</td>
                  <td className="px-4 py-2">
                    {row.narration}
                    {row.fx_amount && row.fx_currency && (
                      <span className="block text-xs text-muted-foreground">
                        {formatMoney(row.fx_amount, row.fx_currency)} @ {row.fx_rate}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {row.source_label ? (
                      <RecordLink source={row} label={row.source_label} />
                    ) : (
                      row.party_label
                    )}
                  </td>
                  <td className={NUM}>{Number(row.debit_inr) ? formatInr(row.debit_inr) : ""}</td>
                  <td className={NUM}>{Number(row.credit_inr) ? formatInr(row.credit_inr) : ""}</td>
                  <td className={NUM}>{formatInr(row.balance_inr)}</td>
                </tr>
              ))}
              <tr className="bg-muted/30 font-medium">
                <td className="px-4 py-2" colSpan={6}>
                  Closing balance
                </td>
                <td className={NUM}>{formatInr(ledger.data.closing_inr)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
